import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-24 h-16 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-xs text-foreground-500">{article.date}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">{article.categoryName}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
            {article.categoryName}
          </span>
          <span className="text-xs text-foreground-500">{article.date}</span>
        </div>
        <h3 className="text-base font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="flex items-center flex-wrap gap-1.5 mt-3">
          {article.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-500">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}